import React, { useState, useEffect } from 'react';
import { diseaseAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FiPlus, FiSearch, FiFilter, FiX, FiEye, FiEdit2, FiTrash2, FiAlertTriangle, FiUploadCloud } from 'react-icons/fi';
import CsvUploader from '../components/CsvUploader';

const COLORS = ['#3b82f6','#ef4444','#f59e0b','#10b981','#8b5cf6','#ec4899'];
const sBtn = (bg='#3b82f6', color='#fff') => ({ padding: '10px 20px', background: bg, color, border: bg==='#f0f9ff'?'1px solid #bfdbfe':'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'inline-flex', alignItems: 'center', gap: 6 });
const sInput = { padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'Inter,sans-serif', width: '100%' };
const sLabel = { display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4 };
const sBadge = (color) => ({ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${color}20`, color });

const Surveillance = () => {
  const { canCreate, canEdit, isAdmin } = useAuth();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [filters, setFilters] = useState({ disease: '', status: '', severity: '', search: '' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState({
    disease: '', category: 'communicable', status: 'suspected', severity: 'mild',
    patientInfo: { age: '', ageGroup: '25-44', gender: 'male', race: '', zipCode: '', county: '', state: '' },
    location: { region: '', district: '', coordinates: { lat: '', lng: '' } },
    onsetDate: '', diagnosisDate: ''
  });

  const load = async () => {
    try {
      const { data } = await diseaseAPI.getAll({ ...filters, page, limit: 15 });
      setCases(data.cases); setTotal(data.total);
    } catch (e) { toast.error('Failed to load cases'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [page, filters]);
  useEffect(() => {
    diseaseAPI.getStats().then(r => setStats(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await diseaseAPI.create(form);
      toast.success('Case created');
      setShowModal(false);
      setForm({ disease: '', category: 'communicable', status: 'suspected', severity: 'mild', patientInfo: { age: '', ageGroup: '25-44', gender: 'male', race: '', zipCode: '', county: '', state: '' }, location: { region: '', district: '', coordinates: { lat: '', lng: '' } }, onsetDate: '', diagnosisDate: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this case?')) return;
    try { await diseaseAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const statusColor = { suspected: '#f59e0b', confirmed: '#ef4444', under_investigation: '#3b82f6', resolved: '#10b981', deceased: '#6b7280' };
  const sevColor = { mild: '#10b981', moderate: '#f59e0b', severe: '#ef4444', critical: '#991b1b' };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>Disease Surveillance</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Monitor, track, and investigate disease cases</p>
        </div>
        {canCreate && <button onClick={() => setShowCsvUpload(true)} style={sBtn('#f0f9ff','#2563eb')}><FiUploadCloud size={16}/> CSV Upload</button>}
        {canCreate && <button onClick={() => setShowModal(true)} style={sBtn()}><FiPlus size={16}/> Report Case</button>}
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 24 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Cases by Status</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Pie data={(stats.byStatus||[]).map(s=>({name:s._id,value:s.count}))} cx="50%" cy="50%" innerRadius={40} outerRadius={75} dataKey="value" label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`}>
                {(stats.byStatus||[]).map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie><Tooltip/></PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Top Diseases</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={(stats.byDisease||[]).slice(0,6).map(d=>({name:d._id,count:d.count}))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/><XAxis type="number" tick={{fontSize:11}}/><YAxis type="category" dataKey="name" width={90} tick={{fontSize:11}}/><Tooltip/><Bar dataKey="count" fill="#3b82f6" radius={[0,4,4,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>By Severity</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Pie data={(stats.bySeverity||[]).map(s=>({name:s._id,value:s.count}))} cx="50%" cy="50%" innerRadius={40} outerRadius={75} dataKey="value" label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`}>
                {(stats.bySeverity||[]).map((_,i)=><Cell key={i} fill={['#10b981','#f59e0b','#ef4444','#991b1b'][i]}/>)}
              </Pie><Tooltip/></PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar" style={{ background: '#fff', borderRadius: 14, padding: '16px 20px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <FiSearch color="#94a3b8" />
        <input placeholder="Search cases..." style={{ ...sInput, maxWidth: 220 }} value={filters.search} onChange={e => { setFilters({...filters, search: e.target.value}); setPage(1); }} />
        <select style={{ ...sInput, maxWidth: 160 }} value={filters.status} onChange={e => { setFilters({...filters, status: e.target.value}); setPage(1); }}>
          <option value="">All Statuses</option>
          {['suspected','confirmed','under_investigation','resolved','deceased'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
        <select style={{ ...sInput, maxWidth: 140 }} value={filters.severity} onChange={e => { setFilters({...filters, severity: e.target.value}); setPage(1); }}>
          <option value="">All Severity</option>
          {['mild','moderate','severe','critical'].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{ fontSize: 13, color: '#64748b', marginLeft: 'auto' }}>{total} cases found</span>
      </div>

      {/* Cases Table */}
      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              {['Case ID','Disease','Status','Severity','Region','Age/Gender','Reported','Actions'].map(h=>(
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {cases.map(c => (
                <tr key={c._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: '#3b82f6' }}>{c.caseId}</td>
                  <td style={{ padding: '12px 16px' }}>{c.disease} {c.isOutbreak && <FiAlertTriangle color="#ef4444" size={14} style={{marginLeft:4}}/>}</td>
                  <td style={{ padding: '12px 16px' }}><span style={sBadge(statusColor[c.status]||'#6b7280')}>{c.status?.replace('_',' ')}</span></td>
                  <td style={{ padding: '12px 16px' }}><span style={sBadge(sevColor[c.severity]||'#6b7280')}>{c.severity}</span></td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{c.location?.region}</td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{c.patientInfo?.age} / {c.patientInfo?.gender}</td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{new Date(c.reportedDate).toLocaleDateString()}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setShowDetail(c)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '6px 8px', cursor: 'pointer' }}><FiEye size={14} color="#3b82f6"/></button>
                      {isAdmin && <button onClick={() => handleDelete(c._id)} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, padding: '6px 8px', cursor: 'pointer' }}><FiTrash2 size={14} color="#ef4444"/></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="pagination-bar" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9' }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>Page {page} of {Math.ceil(total/15) || 1}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={page<=1} onClick={()=>setPage(p=>p-1)} style={{ ...sBtn('#f1f5f9'), color: '#475569', opacity: page<=1?0.5:1 }}>Previous</button>
            <button disabled={page>=Math.ceil(total/15)} onClick={()=>setPage(p=>p+1)} style={{ ...sBtn('#f1f5f9'), color: '#475569', opacity: page>=Math.ceil(total/15)?0.5:1 }}>Next</button>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800 }}>Report Disease Case</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={22}/></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div><label style={sLabel}>Disease*</label><input style={sInput} value={form.disease} onChange={e=>setForm({...form,disease:e.target.value})} required placeholder="e.g., COVID-19"/></div>
                <div><label style={sLabel}>Category</label><select style={sInput} value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                  {['communicable','chronic','environmental','maternal','mental_health','injury','other'].map(c=><option key={c} value={c}>{c.replace('_',' ')}</option>)}
                </select></div>
                <div><label style={sLabel}>Status</label><select style={sInput} value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                  {['suspected','confirmed','under_investigation','resolved'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
                </select></div>
                <div><label style={sLabel}>Severity</label><select style={sInput} value={form.severity} onChange={e=>setForm({...form,severity:e.target.value})}>
                  {['mild','moderate','severe','critical'].map(s=><option key={s} value={s}>{s}</option>)}
                </select></div>
                <div><label style={sLabel}>Patient Age</label><input type="number" style={sInput} value={form.patientInfo.age} onChange={e=>setForm({...form,patientInfo:{...form.patientInfo,age:e.target.value}})}/></div>
                <div><label style={sLabel}>Gender</label><select style={sInput} value={form.patientInfo.gender} onChange={e=>setForm({...form,patientInfo:{...form.patientInfo,gender:e.target.value}})}>
                  <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                </select></div>
                <div><label style={sLabel}>Region</label><select style={sInput} value={form.location.region} onChange={e=>setForm({...form,location:{...form.location,region:e.target.value}})}>
                  <option value="">Select</option>{['Northeast','Southeast','Midwest','Southwest','West Coast','Pacific Northwest'].map(r=><option key={r} value={r}>{r}</option>)}
                </select></div>
                <div><label style={sLabel}>Zip Code</label><input style={sInput} value={form.patientInfo.zipCode} onChange={e=>setForm({...form,patientInfo:{...form.patientInfo,zipCode:e.target.value}})}/></div>
                <div><label style={sLabel}>State</label><input style={sInput} value={form.patientInfo.state} onChange={e=>setForm({...form,patientInfo:{...form.patientInfo,state:e.target.value}})} placeholder="e.g., NY"/></div>
                <div><label style={sLabel}>Race</label><input style={sInput} value={form.patientInfo.race} onChange={e=>setForm({...form,patientInfo:{...form.patientInfo,race:e.target.value}})}/></div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowModal(false)} style={sBtn('#e2e8f0')}>Cancel</button>
                <button type="submit" style={sBtn()}>Create Case</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800 }}>Case: {showDetail.caseId}</h2>
              <button onClick={() => setShowDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={22}/></button>
            </div>
            <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[['Disease', showDetail.disease], ['Category', showDetail.category], ['Status', showDetail.status], ['Severity', showDetail.severity],
                ['Region', showDetail.location?.region], ['State', showDetail.patientInfo?.state],
                ['Age', showDetail.patientInfo?.age], ['Gender', showDetail.patientInfo?.gender],
                ['Race', showDetail.patientInfo?.race], ['Zip Code', showDetail.patientInfo?.zipCode],
                ['Reported', new Date(showDetail.reportedDate).toLocaleDateString()],
                ['Outbreak', showDetail.isOutbreak ? 'Yes' : 'No']
              ].map(([k, v]) => (
                <div key={k} style={{ padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{k}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginTop: 2 }}>{v || '-'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showCsvUpload && (
        <CsvUploader
          title="Disease Cases"
          templateColumns={['disease','category','status','severity','patient_age','gender','race','region','state','zip_code','county','district','age_group','reported_date','is_outbreak']}
          templateSampleRows={[
            ['COVID-19','communicable','confirmed','moderate','45','male','White','Northeast','NY','10001','County 1','District 5','25-44','2024-06-15','false'],
            ['Influenza A','communicable','suspected','mild','32','female','Hispanic','Southeast','FL','33101','County 12','District 2','25-44','2024-07-01','false'],
            ['Tuberculosis','communicable','confirmed','severe','67','male','Black','Midwest','IL','60601','County 8','District 1','65+','2024-08-20','true']
          ]}
          onUpload={(formData) => diseaseAPI.bulkImport(formData)}
          onClose={() => { setShowCsvUpload(false); fetchCases(); }}
        />
      )}
    </div>
  );
};

export default Surveillance;
